import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function Section({ title, desc, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <h2 className="font-head text-lg font-bold">{title}</h2>
      {desc && <p className="mt-0.5 text-sm text-muted-foreground">{desc}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const name = res.headers["content-disposition"]?.match(/filename=(.+)/)?.[1] || "atl-backup.json";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Backup failed. Please try again."); }
    finally { setBackingUp(false); }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Store, shipping and payment configuration</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="rounded-lg bg-primary font-semibold text-primary-foreground hover:bg-[#7CBCE0]">{saving ? "Saving…" : "Save Changes"}</Button>
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Store" desc="Shown to customers in the Telegram bot">
          <div className="space-y-2"><Label>Store name</Label><Input value={s.store_name || ""} onChange={(e) => set("store_name", e.target.value)} data-testid="set-store-name" /></div>
          <div className="space-y-2"><Label>Welcome message</Label><Textarea value={s.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} rows={3} placeholder="Leave blank for default welcome" data-testid="set-welcome" /></div>
          <div className="space-y-2"><Label>Support message</Label><Textarea value={s.support_contact || ""} onChange={(e) => set("support_contact", e.target.value)} rows={2} data-testid="set-support" /></div>
        </Section>

        <Section title="Shipping & Payment Discount" desc="Flat shipping fee and the automatic bank-transfer discount">
          <div className="space-y-2"><Label>Flat-rate shipping (AUD)</Label><Input type="number" step="0.01" min="0" value={s.shipping_cost ?? 0} onChange={(e) => set("shipping_cost", parseFloat(e.target.value) || 0)} data-testid="set-shipping" /></div>
          <p className="text-xs text-muted-foreground">Charged on every order (website and Telegram). Local pickup is turned off.</p>
          <div className="space-y-2">
            <Label>Automatic bank-transfer discount (%)</Label>
            <Input type="number" step="0.5" value={s.auto_discount_percent ?? 0} onChange={(e) => set("auto_discount_percent", parseFloat(e.target.value) || 0)} data-testid="set-auto-discount" />
            <p className="text-xs text-muted-foreground">Applied automatically to every order at checkout since all payments are bank transfer. Set to 0 to disable.</p>
          </div>
        </Section>

        <Section title="Bank Transfer" desc="Payment details shown to customers after checkout">
          <div className="space-y-2"><Label>Account name</Label><Input value={s.account_name || ""} onChange={(e) => set("account_name", e.target.value)} data-testid="set-account-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>BSB</Label><Input value={s.bsb || ""} onChange={(e) => set("bsb", e.target.value)} data-testid="set-bsb" /></div>
            <div className="space-y-2"><Label>Account number</Label><Input value={s.account_number || ""} onChange={(e) => set("account_number", e.target.value)} data-testid="set-account-number" /></div>
          </div>
          <div className="space-y-2"><Label>Bank name (optional)</Label><Input value={s.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} data-testid="set-bank-name" /></div>
          <div className="space-y-2"><Label>Extra instructions</Label><Textarea value={s.bank_instructions || ""} onChange={(e) => set("bank_instructions", e.target.value)} rows={2} data-testid="set-bank-instructions" /></div>
          <p className="text-xs text-muted-foreground">Customers are told to use their order number as the payment reference — product names are never included in the transfer description.</p>
        </Section>

        <Section title="Backup" desc="Download a complete copy of your orders, customers, website members, products, categories, discount codes and settings. Read-only — nothing is changed or removed.">
          <Button onClick={downloadBackup} disabled={backingUp} variant="outline" className="rounded-lg" data-testid="download-backup-btn">{backingUp ? "Preparing backup…" : "Download full backup"}</Button>
        </Section>
      </div>
    </div>
  );
}
